import React, { useState, useEffect } from 'react';
import { Edit, AlertTriangle, CreditCard, DollarSign, Wallet, History, Calendar, ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import api from '../components/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const FraisInscriptionPage = () => {
  const navigate = useNavigate();
  const [fraisInscription, setFraisInscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    action: null
  });
  const [formData, setFormData] = useState({
    frais: ''
  });

  useEffect(() => {
    fetchFraisInscription();
  }, []);

  const fetchFraisInscription = async () => {
    try {
      setLoading(true);
      const response = await api.get('/etablissement/frais-inscription/');
      setFraisInscription(response.data);
      console.log('Frais d\'inscription:', response.data);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.log('Aucun frais d\'inscription trouvé');
        setFraisInscription(null);
      } else {
        console.error('Erreur lors de la récupération des frais d\'inscription:', error);
        toast.error('Erreur lors de la récupération des frais d\'inscription');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    if (fraisInscription) {
      setFormData({
        frais: fraisInscription.frais
      });
    } else {
      setFormData({
        frais: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.frais || isNaN(parseInt(formData.frais)) || parseInt(formData.frais) < 0) {
      toast.error('Le montant des frais d\'inscription doit être un nombre positif');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const dataToSend = {
        frais: parseInt(formData.frais)
      };

      let response;
      if (fraisInscription) {
        response = await api.put('/etablissement/frais-inscription/', dataToSend);
        toast.success('Frais d\'inscription mis à jour avec succès');
      } else {
        response = await api.post('/etablissement/frais-inscription/', dataToSend);
        toast.success('Frais d\'inscription créés avec succès');
      }

      setFraisInscription(response.data);
      handleCloseDialog();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      if (error.response && error.response.data) {
        const errors = error.response.data;
        if (typeof errors === 'object') {
          Object.entries(errors).forEach(([field, message]) => {
            toast.error(`${field}: ${Array.isArray(message) ? message.join(', ') : message}`);
          });
        } else {
          toast.error('Erreur lors de la sauvegarde');
        }
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } finally {
      setLoading(false);
    }
  };

  // Formatter le montant avec le séparateur de milliers
  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  // Formatter la date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="bg-white/10 p-3 rounded-xl mr-4">
                    <CreditCard className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold">Frais d'Inscription</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les frais d'inscription pour votre établissement
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleOpenDialog}
                  className="mt-4 md:mt-0 inline-flex items-center bg-white text-[#202654] px-4 py-2 rounded-lg shadow-md hover:bg-gray-100 transition-all duration-200"
                >
                  <Edit className="h-5 w-5 mr-2" />
                  {fraisInscription ? 'Modifier' : 'Définir'}
                </button>
              </div>
            </div>

            {/* Contenu principal */}
            <div className="p-6">
              <div className="max-w-7xl mx-auto">
                {loading && !fraisInscription ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
                  </div>
                ) : !fraisInscription ? (
                  <div className="bg-white rounded-xl shadow-md p-8 text-center">
                    <CreditCard className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                    <h2 className="text-xl font-semibold text-gray-700 mb-2">Aucun frais d'inscription défini</h2>
                    <p className="text-gray-500 mb-6">
                      Vous n'avez pas encore défini les frais d'inscription pour votre établissement.
                    </p>
                    <button
                      onClick={handleOpenDialog}
                      className="inline-flex items-center bg-[#202654] text-white px-5 py-2 rounded-lg shadow-md hover:bg-[#2d347d] transition-all duration-200"
                    >
                      <Edit className="h-5 w-5 mr-2" />
                      Définir les frais d'inscription
                    </button>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="p-8">
                      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8">
                        <div className="flex flex-col">
                          <h2 className="text-lg font-medium text-gray-500">Frais d'inscription actuels</h2>
                          <div className="flex items-center mt-2">
                            <Wallet className="h-8 w-8 text-[#202654] mr-3" />
                            <span className="text-3xl font-bold text-[#202654]">
                              {formatAmount(fraisInscription.frais)} FCFA
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={handleOpenDialog}
                          className="mt-4 md:mt-0 inline-flex items-center bg-[#202654] text-white px-4 py-2 rounded-lg shadow-sm hover:bg-[#2d347d] transition-all duration-200"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Modifier
                        </button>
                      </div>

                      <div className="border-t border-gray-100 pt-6">
                        <h3 className="text-lg font-medium text-gray-700 mb-4">Historique</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="flex items-start">
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <Calendar className="h-6 w-6 text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <h4 className="text-sm font-medium text-gray-500">Date d'ajout</h4>
                              <p className="text-base font-medium">
                                {formatDate(fraisInscription.date_ajout)}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start">
                            <div className="bg-green-50 p-3 rounded-lg">
                              <History className="h-6 w-6 text-green-600" />
                            </div>
                            <div className="ml-4">
                              <h4 className="text-sm font-medium text-gray-500">Dernière modification</h4>
                              <p className="text-base font-medium">
                                {formatDate(fraisInscription.date_modif)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal pour modifier les frais d'inscription */}
        {dialogOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-800">
                    {fraisInscription ? 'Modifier les frais d\'inscription' : 'Définir les frais d\'inscription'}
                  </h2>
                  <button
                    onClick={handleCloseDialog}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Montant (FCFA)
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        min="0"
                        step="500"
                        name="frais"
                        value={formData.frais}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
                        placeholder="Montant des frais d'inscription"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Montant que chaque élève devra payer lors de l'inscription
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 border-t bg-gray-50 flex justify-end space-x-3 rounded-b-xl">
                <button
                  onClick={handleCloseDialog}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-4 py-2 bg-[#202654] text-white rounded-md hover:bg-[#2d347d] transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading && (
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {fraisInscription ? 'Mettre à jour' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FraisInscriptionPage;
